import { gated, isPlaceholderPhone, isPlaceholderPiva, isPlaceholderText } from '../sedi/placeholders'
import type { SedeConfig } from '../sedi/types'
import { legalPathFor } from './paths'
import type { LegalEntity, LegalLocale } from './types'

/** `https://` + the sede's first domain — canonical, hreflang and OG URLs hang off it. */
export function legalOrigin(sede: SedeConfig): string {
  return `https://${sede.domains[0]}`
}

/** '2026-09-23' → '23 settembre 2026' / '23 September 2026'. UTC, so the build machine's timezone never shifts the day. */
export function formatLegalDate(iso: string, locale: LegalLocale): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) throw new Error(`legalPages.lastUpdated must be YYYY-MM-DD, got "${iso}"`)
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return new Intl.DateTimeFormat(locale === 'it' ? 'it-IT' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

/**
 * The data controller as the legal pages print it, from the sede config.
 * Placeholder street / CAP / P.IVA / phone come back `undefined` (or drop out
 * of `cityLine`) and their lines simply do not render — a legal page must
 * never show "TBD", zeros or a borrowed address. A missing legal name is not
 * gated but fatal: the page would not name who controls the data.
 *
 * Pure: the same sede and locale always give the same entity.
 */
export function legalEntityFor(sede: SedeConfig, locale: LegalLocale): LegalEntity {
  const cfg = sede.legalPages
  if (!cfg) throw new Error(`${sede.slug}: no legalPages in the sede config`)
  const legalName = gated(sede.legal?.legalName, isPlaceholderText)
  if (!legalName) {
    throw new Error(`${sede.slug}: legal.legalName is missing or a placeholder — a legal page must name the data controller`)
  }
  const postalCode = gated(sede.contact.postalCode, isPlaceholderText)
  return {
    legalName,
    legalForm: cfg.legalForm,
    street: gated(sede.contact.addressStreet, isPlaceholderText),
    cityLine: [postalCode, cfg.city].filter(Boolean).join(' '),
    // `legal.piva` first, `contact.piva` as the fallback, each gated on its own.
    piva: gated(sede.legal?.piva, isPlaceholderPiva) ?? gated(sede.contact.piva, isPlaceholderPiva),
    privacyEmail: sede.contact.privacyEmail,
    email: sede.contact.email,
    phone: gated(sede.contact.phone, isPlaceholderPhone),
    domain: sede.domains[0],
    privacyPath: legalPathFor('privacy', locale),
    cookiePath: legalPathFor('cookie', locale),
    deletionPath: legalPathFor('dataDeletion', locale),
    lastUpdated: formatLegalDate(cfg.lastUpdated, locale),
    whatsappAi: cfg.whatsappAi === true,
    googlePlaces: cfg.googlePlaces === true,
    platformProvider: gated(cfg.platformProvider?.trim() || undefined, isPlaceholderText),
  }
}
